import { jsx as _jsx } from "react/jsx-runtime";
import BoardElementComponent from '@/components/Board/Elements/BoardElement';
/**
 * Быстрое хеширование props без использования JSON.stringify.
 * Использует только ключевые поля, которые влияют на рендеринг.
 */
const hashProps = (props) => {
    var _a;
    if (!props)
        return '';
    // Собираем только ключевые поля для быстрого хеширования
    const keys = [];
    if (props.x !== undefined)
        keys.push(`x:${props.x}`);
    if (props.y !== undefined)
        keys.push(`y:${props.y}`);
    if (props.w !== undefined)
        keys.push(`w:${props.w}`);
    if (props.h !== undefined)
        keys.push(`h:${props.h}`);
    if (props.rotation !== undefined)
        keys.push(`r:${props.rotation}`);
    if (props.opacity !== undefined)
        keys.push(`o:${props.opacity}`);
    if (props.scaleX !== undefined)
        keys.push(`sx:${props.scaleX}`);
    if (props.scaleY !== undefined)
        keys.push(`sy:${props.scaleY}`);
    if (props.zIndex !== undefined)
        keys.push(`z:${props.zIndex}`);
    if (props.shape !== undefined)
        keys.push(`sh:${props.shape}`);
    if (props.text !== undefined)
        keys.push(`t:${(_a = props.text) === null || _a === void 0 ? void 0 : _a.substring(0, 50)}`); // Ограничиваем длину текста
    if (props.color !== undefined)
        keys.push(`c:${props.color}`);
    if (props.stroke !== undefined)
        keys.push(`st:${props.stroke}`);
    if (props.strokeWidth !== undefined)
        keys.push(`sw:${props.strokeWidth}`);
    if (props.strokeEnabled !== undefined)
        keys.push(`se:${props.strokeEnabled ? '1' : '0'}`);
    if (props.fill !== undefined)
        keys.push(`f:${props.fill}`);
    if (props.fontSize !== undefined)
        keys.push(`fs:${props.fontSize}`);
    if (props.fontFamily !== undefined)
        keys.push(`ff:${props.fontFamily}`);
    if (props.fontStyle !== undefined)
        keys.push(`fst:${props.fontStyle}`);
    if (props.textAlign !== undefined)
        keys.push(`ta:${props.textAlign}`);
    if (props.verticalAlign !== undefined)
        keys.push(`va:${props.verticalAlign}`);
    if (props.wrap !== undefined)
        keys.push(`wr:${props.wrap}`);
    if (props.lineHeight !== undefined)
        keys.push(`lh:${props.lineHeight}`);
    if (props.padding !== undefined)
        keys.push(`p:${props.padding}`);
    if (props.crop) {
        const c = props.crop;
        keys.push(`cr:${c.x || 0},${c.y || 0},${c.width || 0},${c.height || 0}`);
    }
    if (props.clip) {
        const cl = props.clip;
        if (cl.type === 'circle')
            keys.push(`clc:${cl.r || 0}`);
        if (cl.type === 'rect')
            keys.push(`clr:${cl.x || 0},${cl.y || 0},${cl.w || 0},${cl.h || 0}`);
    }
    if (props.localGrid) {
        const lg = props.localGrid;
        keys.push(`lg:${lg.enabled ? '1' : '0'},${lg.size || 0},${lg.color || ''}`);
    }
    if (props.filters) {
        const fs = props.filters;
        keys.push(`fs:${fs.brightness || 0},${fs.contrast || 0},${fs.opacity || 0}`);
    }
    return keys.join('|');
};
/**
 * Вычисляет хеш элемента для отслеживания изменений.
 * Оптимизированная версия без использования JSON.stringify.
 * Хеш включает все свойства элемента, которые влияют на рендеринг.
 */
export const computeElementHash = (el) => {
    const propsHash = hashProps(el.props);
    return `${el.id}-${el.elementType}-${el.locked ? '1' : '0'}-${el.description || ''}-${propsHash}`;
};
/**
 * Рендерит слои и элементы доски с оптимизацией для предотвращения лишних перерендеров
 *
 * @param params - Параметры для рендеринга
 * @returns Массив отрендеренных слоев
 */
export const renderLayers = ({ boardState, selectedIds, selectedIdsSet, draftSelectionIds, canEdit, stageMode, elementDragBoundFunc, handleElementDoubleClick, handleDescriptionIconClick, editingTextElement, viewport, elementHashesRef, previousSelectedIdsRef, previousDraftSelectionRef, renderedLayersRef, renderedElementsMapRef, isPanning, isZooming, isTransforming = false, isCropping = false, movingElementsIds = [], }) => {
    var _a;
    const { zoom, position: stagePosition, size: stageSize } = viewport;
    const draftSelectionSet = new Set(draftSelectionIds);
    const previousSelectedSet = previousSelectedIdsRef.current;
    const previousDraftSet = previousDraftSelectionRef.current;
    // Определяем, какие элементы изменили состояние выделения
    const selectionChangedIds = new Set();
    // Проверяем все элементы в текущем выделении
    for (const id of selectedIds) {
        // Элемент изменил состояние, если:
        // 1. Он не был в selectedIds ранее
        // 2. Он был в draftSelectionIds (переход из draft в selected)
        if (!previousSelectedSet.has(id)) {
            selectionChangedIds.add(id);
        }
        else if (previousDraftSet.has(id)) {
            // Элемент был в draft, а теперь в selected - это изменение
            selectionChangedIds.add(id);
        }
    }
    for (const id of draftSelectionIds) {
        // Элемент изменил состояние, если:
        // 1. Он не был в draftSelectionIds ранее
        // 2. Он был в selectedIds (переход из selected в draft)
        if (!previousDraftSet.has(id)) {
            selectionChangedIds.add(id);
        }
        else if (previousSelectedSet.has(id)) {
            // Элемент был в selected, а теперь в draft - это изменение
            selectionChangedIds.add(id);
        }
    }
    // Проверяем элементы, которые были выделены, но теперь не выделены
    for (const id of previousSelectedSet) {
        // Элемент изменил состояние, если он был в selectedIds, но теперь не в selectedIds
        if (!selectedIdsSet.has(id)) {
            selectionChangedIds.add(id);
        }
    }
    for (const id of previousDraftSet) {
        // Элемент изменил состояние, если он был в draftSelectionIds, но теперь не в draftSelectionIds
        if (!draftSelectionSet.has(id)) {
            selectionChangedIds.add(id);
        }
    }
    // Создаем Map для быстрого поиска элементов по ID
    const elementsMap = (renderedElementsMapRef === null || renderedElementsMapRef === void 0 ? void 0 : renderedElementsMapRef.current) || new Map();
    if (!renderedElementsMapRef) {
        // Если ref не передан, создаем временный Map
        if (renderedLayersRef.current) {
            for (const layer of renderedLayersRef.current) {
                if (!layer)
                    continue;
                for (const element of layer) {
                    if (element && element.key) {
                        const id = typeof element.key === 'string' ? parseInt(element.key, 10) : element.key;
                        if (!isNaN(id)) {
                            elementsMap.set(id, element);
                        }
                    }
                }
            }
        }
    }
    // Рендерим слои и элементы
    const result = ((_a = boardState.layers) === null || _a === void 0 ? void 0 : _a.filter((layer) => layer.visible).sort((a, b) => a.zIndex - b.zIndex).map((layer) => layer.elements
        .sort((a, b) => { var _a, _b; return ((_a = a.zIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.zIndex) !== null && _b !== void 0 ? _b : 0); })
        .map((el) => {
        // Если элемент редактируется, не рендерим его обычным способом
        if (editingTextElement && el.id === editingTextElement.id) {
            return null;
        }
        // Вычисляем хеш элемента
        const elementHash = computeElementHash(el);
        const previousHash = elementHashesRef.current.get(el.id);
        // Проверяем, нужно ли перерендерить элемент
        const elementChanged = previousHash !== elementHash;
        const selectionChanged = selectionChangedIds.has(el.id);
        // Вычисляем состояние выделения для этого элемента
        const isSelected = selectedIdsSet.has(el.id);
        const isDraftSelected = draftSelectionSet.has(el.id);
        const showSelectionBorder = isDraftSelected ||
            (selectedIds.length > 1 && isSelected) ||
            (isSelected && el.locked);
        // Проверяем, перемещается ли элемент
        const isMoving = movingElementsIds.includes(el.id);
        // Если элемент не изменился и его выделение не изменилось,
        // пытаемся переиспользовать предыдущий результат
        // НО: если элемент начал или закончил перемещение, нужно перерендерить для скрытия/показа иконки
        if (!elementChanged && !selectionChanged && !isMoving) {
            const prevElement = elementsMap.get(el.id);
            if (prevElement) {
                // Сохраняем новый хеш и переиспользуем элемент
                elementHashesRef.current.set(el.id, elementHash);
                return prevElement;
            }
        }
        // Сохраняем новый хеш
        elementHashesRef.current.set(el.id, elementHash);
        // Рендерим элемент
        const element = (_jsx(BoardElementComponent, { el: el, selectedIds: selectedIds, draftSelection: draftSelectionIds, draggable: canEdit &&
                !el.locked &&
                !(layer.locked || false) &&
                stageMode !== 'move' &&
                stageMode !== 'ruler', layer: layer, elementDragBoundFunc: elementDragBoundFunc, onElementDoubleClick: handleElementDoubleClick, onDescriptionIconClick: handleDescriptionIconClick, elementHash: elementHash, isSelected: isSelected, isDraftSelected: isDraftSelected, showSelectionBorder: showSelectionBorder, zoom: zoom, stagePosition: stagePosition, viewportSize: stageSize, isPanning: isPanning, isZooming: isZooming, isTransforming: isTransforming || isCropping, isMoving: isMoving }, el.id));
        // Сохраняем в Map для быстрого поиска
        elementsMap.set(el.id, element);
        return element;
    }))) || [];
    // Обновляем refs для следующего рендера
    previousSelectedIdsRef.current = selectedIdsSet;
    previousDraftSelectionRef.current = draftSelectionSet;
    // Сохраняем результат в ref для использования во время взаимодействия
    renderedLayersRef.current = result;
    // Обновляем Map элементов для следующего рендера
    if (renderedElementsMapRef) {
        renderedElementsMapRef.current = elementsMap;
    }
    return result;
};
